import {
  OrganizationListResponseV1Schema,
  OrganizationSwitchResponseV1Schema,
  type OrganizationListItemV1,
} from "@fulcrum5/suite-contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";

type OrganizationsState =
  | { readonly kind: "loading"; readonly items: readonly OrganizationListItemV1[]; readonly nextCursor: string | null }
  | { readonly kind: "ready"; readonly items: readonly OrganizationListItemV1[]; readonly nextCursor: string | null }
  | {
    readonly kind: "error";
    readonly items: readonly OrganizationListItemV1[];
    readonly nextCursor: string | null;
    readonly message: string;
    readonly canRetry: boolean;
  };

const WRAPPABLE_VALUE_STYLE = { minWidth: 0, overflowWrap: "anywhere" } as const;

function roleLabel(role: OrganizationListItemV1["organization"]["role"]): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function errorState(error: unknown): Pick<Extract<OrganizationsState, { readonly kind: "error" }>, "message" | "canRetry"> {
  if (error instanceof PlatformApiError) {
    switch (error.status) {
      case 403:
        return { message: "You do not have permission to view organizations.", canRetry: false };
      case 409:
        return { message: "Organization access changed. Refresh and try again.", canRetry: true };
      case 422:
        return { message: "The organization request could not be completed.", canRetry: false };
      case 503:
        return { message: "Organizations are temporarily unavailable.", canRetry: true };
      default:
        return { message: "Organizations could not be loaded.", canRetry: error.retryable };
    }
  }
  return { message: "Organizations could not be loaded.", canRetry: true };
}

function navigateToOrganizations(): void {
  if (typeof window === "undefined") return;
  window.history.replaceState({}, "", "/organizations");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function activeItems(items: readonly OrganizationListItemV1[]): readonly OrganizationListItemV1[] {
  return items.filter((item) => item.organization.status === "active");
}

function appendOrganizations(
  current: readonly OrganizationListItemV1[],
  page: readonly OrganizationListItemV1[],
): readonly OrganizationListItemV1[] {
  const known = new Set(current.map((item) => item.organization.id));
  return [...current, ...page.filter((item) => !known.has(item.organization.id))];
}

export function OrganizationsPage(): JSX.Element {
  const session = usePlatformSession();
  const [state, setState] = useState<OrganizationsState>({ kind: "loading", items: [], nextCursor: null });
  const [switchingOrganizationId, setSwitchingOrganizationId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<{ readonly id: string; readonly message: string } | null>(null);
  const listRequestVersion = useRef(0);
  // Keep the latest API on a ref so the mount effect does not re-fire (and race)
  // every time PlatformSessionProvider rebuilds the PlatformApi instance.
  const apiRef = useRef(session.api);
  apiRef.current = session.api;

  const loadOrganizations = useCallback(async ({
    cursor = null,
    append = false,
  }: {
    readonly cursor?: string | null;
    readonly append?: boolean;
  } = {}) => {
    const requestVersion = listRequestVersion.current + 1;
    listRequestVersion.current = requestVersion;
    setState((current) => ({ kind: "loading", items: current.items, nextCursor: current.nextCursor }));
    try {
      // Intentionally no AbortSignal: abort-on-effect-cleanup races leave this
      // page permanently on "Loading organizations." when the first response is
      // discarded and no replacement request commits. Stale responses are
      // ignored via listRequestVersion instead.
      const result = await apiRef.current.request({
        path: cursor ? `/api/organizations?cursor=${encodeURIComponent(cursor)}` : "/api/organizations",
        responseSchema: OrganizationListResponseV1Schema,
      });
      if (listRequestVersion.current !== requestVersion) return;
      const page = activeItems(result.data.items);
      setState((current) => ({
        kind: "ready",
        items: append ? appendOrganizations(current.items, page) : page,
        nextCursor: result.data.nextCursor,
      }));
    } catch (error) {
      if (listRequestVersion.current !== requestVersion) return;
      const mapped = errorState(error);
      setState((current) => ({ kind: "error", items: current.items, nextCursor: current.nextCursor, ...mapped }));
    }
  }, []);

  useEffect(() => {
    if (session.phase !== "ready" || !session.identity?.platformSessionId) return;
    void loadOrganizations();
  }, [loadOrganizations, session.identity?.platformSessionId, session.phase]);

  const switchOrganization = useCallback(async (organizationId: string) => {
    setSwitchingOrganizationId(organizationId);
    setSwitchError(null);
    try {
      await apiRef.current.request({
        path: `/api/organizations/${organizationId}/switch`,
        method: "POST",
        body: { version: 1 },
        responseSchema: OrganizationSwitchResponseV1Schema,
      });
      await session.refresh();
      navigateToOrganizations();
    } catch (error) {
      const mapped = errorState(error);
      setSwitchError({ id: organizationId, message: mapped.message });
    } finally {
      setSwitchingOrganizationId(null);
    }
  }, [session]);

  const hasItems = state.items.length > 0;

  return (
    <section className="platform-page organizations-page" aria-labelledby="organizations-page-title">
      <div className="organizations-page__heading">
        <p className="platform-page__eyebrow organizations-page__eyebrow">Workspace</p>
        <h1 id="organizations-page-title">Organizations</h1>
        <p className="platform-page__summary organizations-page__summary">Your active organization memberships.</p>
      </div>

      {state.kind === "loading" && !hasItems ? (
        <p className="organizations-page__status" role="status">Loading organizations.</p>
      ) : null}

      {state.kind === "error" ? (
        <div className="organizations-page__error" role="alert">
          <p>{state.message}</p>
          {state.canRetry ? (
            <button className="organizations-page__retry" type="button" onClick={() => void loadOrganizations()}>
              Retry organizations
            </button>
          ) : null}
        </div>
      ) : null}

      {state.kind === "ready" && !hasItems ? (
        <p className="organizations-page__empty" role="status">No active organizations are available.</p>
      ) : null}

      {hasItems ? (
        <ul className="organizations-page__list" aria-label="Active organizations" aria-busy={state.kind === "loading"}>
          {state.items.map((item) => {
            const { organization } = item;
            const isCurrent = session.identity?.activeOrganizationId === organization.id;
            const isSwitching = switchingOrganizationId === organization.id;
            return (
              <li className="organizations-page__item" key={organization.id}>
                <article className="organizations-page__row" aria-busy={isSwitching}>
                  <div className="organizations-page__identity" style={WRAPPABLE_VALUE_STYLE}>
                    <h2 className="organizations-page__name" style={WRAPPABLE_VALUE_STYLE}>{organization.name}</h2>
                    <dl className="organizations-page__metadata">
                      <div>
                        <dt>Role</dt>
                        <dd>{roleLabel(organization.role)}</dd>
                      </div>
                      <div>
                        <dt>Account status</dt>
                        <dd>Active</dd>
                      </div>
                      <div>
                        <dt>Recent activity</dt>
                        <dd>Not available</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="organizations-page__actions" style={WRAPPABLE_VALUE_STYLE}>
                    <button
                      className="organizations-page__switch"
                      type="button"
                      disabled={isCurrent || switchingOrganizationId !== null}
                      onClick={() => void switchOrganization(organization.id)}
                      style={WRAPPABLE_VALUE_STYLE}
                    >
                      {isCurrent ? "Current organization" : isSwitching ? "Switching organization" : `Switch to ${organization.name}`}
                    </button>
                    {switchError?.id === organization.id ? (
                      <p className="organizations-page__switch-error" role="alert">{switchError.message}</p>
                    ) : null}
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      ) : null}
      {state.nextCursor ? (
        <button
          className="platform-button platform-button--secondary organizations-page__load-more"
          disabled={state.kind === "loading" || switchingOrganizationId !== null}
          onClick={() => { void loadOrganizations({ cursor: state.nextCursor, append: true }); }}
          type="button"
        >
          Load more organizations
        </button>
      ) : null}
    </section>
  );
}
